import type { World } from '../sim/types';
import { ECONOMY } from '../sim/economy';
import { ISLAND_NAMES, REEF_ISLANDS, type ReefIsland } from '../sim/reef';
import { RECENT_DAYS, coralVerdict, reefCoralHistory, type CoralVerdict } from './reefHealth';
import { patrolCut } from './actionWorth';
import { patrolHoursAdded } from '../sim/management';
import { FUNDS_MONTHS, HEAT_ALERT_DHW, MONTH_DAYS, atSea, busyOn, consultationCost, dollars, isCleanupHome, freeLiaison, monthsInHand, anyProtectionJob, recurringPerMonth, ruleWords, surveyCost, volunteersOn } from '../sim/operations';
import { concernsOf } from '../sim/areas';
import { nextGrantDay, runwayWords, yearMoney } from './runway';
import { SEASON, currentObjective } from '../sim/campaign';
import { civilFromEpochDay, dateLabel, epochDayFromCivil, epochDayOf } from '../sim/calendar';
import type { SurveyKind } from '../sim/economy';
import { CLEARED_T_PER_DAY, shoreDebrisT } from '../sim/shoreDebris';

/**
 * The sanctuary's goals (M278, `docs/STRATEGIC_PLAY.md` section 1: "broad objectives ... different actions contribute
 * to the same objective ... a recommended option that does not remove the choice"), in place of one objective at a
 * time once the first chapter's guidance is done. Each is read from the world as it stands, none stored: where it
 * stands, met or not, the way the game recommends now, and the other ways to it. The four the brief names: knowledge of
 * an under-monitored reef, a human pressure eased (the fishing on the grazing fish, the one the model holds by island),
 * the warm season prepared for, and the program kept solvent.
 */
export type GoalId = 'decline' | 'pressure' | 'evaluate' | 'season' | 'money' | 'shore';
export interface Goal {
  id: GoalId;
  title: string;
  /** Where it stands, in a sentence. */
  state: string;
  level: 'met' | 'doing' | 'open';
  /** The way recommended now, a HUD act (`area:<island>`, `respond:<island>` or a page's kind); none where nothing is to be done now (met, or waiting on work under way). */
  way: { label: string; act: string } | null;
  /** The other ways to it, in words. */
  others: string;
  /** What the way recommended costs, and what choosing it gives up (M287, the brief's 7.3: each choice's opportunity cost). */
  cost?: string;
  gives?: string;
}

/**
 * Where the conservancy's liaisons are, when none is free (M311, the fourth playtest's: "my liaison was occupied by that
 * training", the guidance still offering the protection): what the first one free does, the day it is free, and how
 * many are employed; none where a liaison is free now. With none employed, what is told is that one is needed (its
 * review: the island's step still offered the protected area with no liaison at all).
 */
export function liaisonBusy(world: World): { doing: string; until: number | null; count: number } | null {
  const ops = world.operations;
  const liaisons = ops.staff.filter((s) => s.role === 'liaison');
  if (liaisons.length === 0) return { doing: '', until: null, count: 0 };
  if (freeLiaison(ops) !== undefined) return null;
  const doingOf = (j: NonNullable<ReturnType<typeof busyOn>>): string => {
    if (j.kind === 'community') return j.island ? `trains ${ISLAND_NAMES[j.island]}'s volunteers` : 'trains Maui Nui\'s volunteers';
    // The rule's island named, or the limit's kind, never its words (its review: a bag limit's own colon and figure read as part of the sentence).
    if (j.rule?.type === 'protect_herbivores') return j.rule.island === 'all' ? `consults Maui Nui's communities on ${j.rule.protect ? 'its protected area' : 'lifting its protected area'}` : `consults ${ISLAND_NAMES[j.rule.island]} on ${j.rule.protect ? 'its protected area' : 'lifting its protected area'}`;
    return j.rule ? 'consults on a fishing limit' : 'is at work';
  };
  const busy = liaisons.flatMap((s) => { const j = busyOn(ops, s.id); return j ? [j] : []; })
    // Free on the day its phase ends, the tick's end of it that morning (measured: 31 January for volunteers begun 1 January).
    .map((j) => ({ until: j.phaseEnds[0] as number, doing: doingOf(j), count: liaisons.length }))
    .sort((a, b) => a.until - b.until);
  return busy[0] ?? null;
}

/** "Your liaison trains Maui's volunteers until 31 January; a protected area waits until then" (M311), or that one is needed. */
const liaisonWaitWords = (b: { doing: string; until: number | null; count: number }): string => b.count === 0
  ? 'the conservancy employs no liaison; a protected area waits for one hired'
  : b.count > 1 ? `both your liaisons are busy, the first free on ${dateLabel(b.until as number)} (it ${b.doing}); a protected area waits until then`
  : `your liaison ${b.doing} until ${dateLabel(b.until as number)}; a protected area waits until then`;

/** The island Research opens first: one the record flags and the conservancy has not counted (a decline before an unknown), else the least known. */
export function researchIsland(world: World): ReefIsland {
  const flagged = REEF_ISLANDS.map((island) => ({ island, c: concernsOf(world, island), ours: world.operations.areaCounts.some((x) => x.island === island && x.kind === 'cover') }));
  return flagged.find((f) => f.c?.decline && !f.ours)?.island ?? flagged.find((f) => f.c?.uncertain)?.island ?? 'maui';
}

/**
 * The days an island's own count stays news (JUDGED, a game's: a year, so each count reads the reef since its last warm
 * season; the conservancy's own watch, closer than NOAA's surveys, which come every three years, McCoy and Shantz 2026).
 */
const KNOWN_DAYS = RECENT_DAYS;

/**
 * Whether the conservancy or its volunteers counted an island's grazing fish within the year (the reef's coral is
 * `reefCountedLately`'s, M327).
 */
const countedLately = (world: World, island: ReefIsland, kind: 'herbivores'): boolean => {
  const today = epochDayOf(world);
  return world.operations.areaCounts.some((c) => c.island === island && c.kind === kind && c.by !== 'noaa' && c.day > today - KNOWN_DAYS);
};

/** The coral survey's cost and the boat's days, as a goal's way tells them (the island card's default plan, 10 days and two divers). */
export const SURVEY_DAYS = 10;
const surveyWords = (kind: SurveyKind): string => `${dollars(surveyCost(kind, SURVEY_DAYS))}, a boat ${SURVEY_DAYS} days`;
/** What the boat's days cost: behind the trips already planned it waits its turn (the plan sails first in, first out; M309's review: it had said the planned trips wait), or another island's survey waits if none is. */
const boatTradeoff = (world: World): string => (world.operations.plan.length > 0
  ? `it waits behind the ${world.operations.plan.length} ${world.operations.plan.length === 1 ? 'trip' : 'trips'} already planned`
  : atSea(world.operations).length >= world.operations.boats ? 'it waits for a boat to come home' : 'no other survey can use that boat meanwhile');

/** Whether the conservancy counted the whole reef's coral (every island at every depth, `reefCoralHistory`) within the year (M327, one reef). */
const reefCountedLately = (world: World): boolean => {
  const today = epochDayOf(world);
  return reefCoralHistory(world).some((p) => p.by === 'conservancy' && p.day > today - KNOWN_DAYS);
};

/** Whether the conservancy or its volunteers counted every island's grazing fish within the year (M327). */
const reefFishCountedLately = (world: World): boolean => REEF_ISLANDS.every((island) => countedLately(world, island, 'herbivores'));

/** Whether a survey of the whole reef (no island) of a kind is at sea or in the plan (M327). */
const reefUnderway = (world: World, kind: SurveyKind): boolean => {
  const ops = world.operations;
  return atSea(ops).some((j) => j.survey === kind && j.island === null) || ops.plan.some((p) => p.kind === kind && p.island === null);
};

/** The islands NOAA's record shows fallen past their error, the largest fall first: what the reef's coral record holds beneath its whole. */
const fallenIslands = (world: World): ReefIsland[] => {
  const drop = (v: CoralVerdict): number => (v.trend === 'down' && v.against && v.last ? v.against.value - v.last.value : 0);
  return REEF_ISLANDS.map((island) => ({ island, v: coralVerdict(world, island, true) })).filter((x) => x.v.trend === 'down').sort((a, b) => drop(b.v) - drop(a.v)).map((x) => x.island);
};

/**
 * Whether the coral is still falling (M287, the brief's "improve monitoring of an important ecological concern"; M327,
 * one reef: the whole reef counted, its islands' falls on NOAA's record the reason): met once the conservancy has counted
 * the reef within the year, or where nothing on the record fell. A survey is asked only where the record shows a loss
 * whose course since is unknown, never to fill a checklist.
 */
function decline(world: World): Goal {
  const fallen = fallenIslands(world);
  const v = coralVerdict(world, 'reef', true);
  const counted = reefCountedLately(world);
  const going = !counted && reefUnderway(world, 'reefBenthic');
  // "Maui's, Lānaʻi's and Molokaʻi's", the largest fall first.
  const owned = fallen.map((x) => `${ISLAND_NAMES[x]}'s`);
  const names = owned.length > 1 ? `${owned.slice(0, -1).join(', ')} and ${owned.at(-1)}` : (owned[0] ?? '');
  return {
    id: 'decline', title: 'Find out whether the coral is still falling',
    state: fallen.length === 0 ? 'No part of the reef\'s coral has fallen on the record.'
      : counted ? 'The reef\'s coral counted again within the year.'
      : `${v.last ? `Maui Nui Reef's live coral was about ${v.last.value.toFixed(0)}% in NOAA's ${v.last.year} survey` : 'The reef\'s coral is not yet counted whole'}; ${names} coral fell past the counts' error${going ? '. A coral survey is under way' : ''}.`,
    level: fallen.length === 0 || counted ? 'met' : going ? 'doing' : 'open',
    way: fallen.length > 0 && !counted && !going ? { label: 'Count the reef\'s coral', act: 'reef:survey:reefBenthic' } : null,
    others: `Only a coral survey measures the coral.${v.last ? ` NOAA's divers last surveyed the whole reef in ${v.last.year}.` : ''}`,
    cost: fallen.length > 0 && !counted && !going ? surveyWords('reefBenthic') : undefined,
    gives: fallen.length > 0 && !counted && !going ? boatTradeoff(world) : undefined,
  };
}

/**
 * Whether the protection works (M287, the brief's "measurable operational or ecological outcomes"; M327, one reef):
 * once fishing is eased anywhere on the reef, the grazing fish counted across it by the conservancy or its volunteers
 * within the year, the evidence that would show them recovering.
 */
function evaluate(world: World): Goal | null {
  const m = world.sanctuaries[0]?.management;
  if (!m) return null;
  const eased = REEF_ISLANDS.some((_, i) => m.herbivoreProtection[i] === true || (m.patrols[i] ?? 0) > 0);
  if (!eased) return null;
  const counted = reefFishCountedLately(world);
  const volunteers = REEF_ISLANDS.every((island) => volunteersOn(world.operations, island));
  const going = !counted && (reefUnderway(world, 'reefFish') || volunteers);
  return {
    id: 'evaluate', title: 'Measure whether your protection works',
    state: counted ? 'The reef\'s grazing fish counted within the year.' : going ? 'A count of the reef\'s grazing fish is under way.'
      : 'The reef\'s grazing fish are protected or patrolled but not counted within the year: without a count, no one can tell whether they are recovering.',
    level: counted ? 'met' : going ? 'doing' : 'open',
    way: counted || going ? null : { label: 'Count the reef\'s grazing fish', act: 'reef:survey:reefFish' },
    others: `Volunteers count the grazing fish on every island every month, no boat needed. At Kahekili, Maui's herbivore reserve, the bullethead parrotfish took about six years to more than triple (Williams et al. 2016): expect years, not months.`,
    cost: counted || going ? undefined : surveyWords('reefFish'),
    gives: counted || going ? undefined : boatTradeoff(world),
  };
}

function pressure(world: World): Goal {
  const m = world.sanctuaries[0]?.management;
  const protectedAll = m?.herbivoreProtection.every(Boolean) ?? false;
  const patrolled = REEF_ISLANDS.filter((_, i) => (m?.patrols[i] ?? 0) > 0);
  // A proposal to protect is the pressure under way, never one to lift it; either holds the reef's proposal back, as the
  // core refuses one beside another (`sameTarget`; its second review).
  const job = anyProtectionJob(world.operations);
  const coming = job?.rule?.type === 'protect_herbivores' && job.rule.protect;
  // The proposal under way by its own words (its second review: an island's, an older save's, had read as Maui Nui's).
  const inProcess = job?.rule ? `${ruleWords(job.rule)} in process` : '';
  const next = nextStretch(world)?.island ?? null;
  // One stretch of coast's patrol, a month's officers' hours (the reef card's own offer).
  const patrolMonth = next ? Math.round(patrolHoursAdded(next, 1) * ECONOMY.officerHour * MONTH_DAYS / 365) : 0;
  const liaisonFree = freeLiaison(world.operations) !== undefined;
  const noLiaison = !world.operations.staff.some((s) => s.role === 'liaison');
  const consult = consultationCost({ type: 'protect_herbivores', island: 'all', protect: true });
  const held = protectedAll || patrolled.length > 0;
  return {
    id: 'pressure', title: 'Ease the fishing on the grazing fish',
    state: protectedAll ? 'A protected area in force across Maui Nui.'
      : patrolled.length > 0 ? `${patrolled.map((i) => ISLAND_NAMES[i]).join(', ')}: a patrol on the water.${coming ? ` ${inProcess}.` : ''}`
      : coming ? `${inProcess}; nothing in force yet.`
      : 'The reef\'s grazing fish protected only by Hawaiʻi\'s own rules.',
    level: held ? 'met' : coming ? 'doing' : 'open',
    // With no liaison free, the patrol is the way (M311: the protected area's press was refused); a patrol alone only
    // while the funds keep half a year after it (M317, G7) and the year's money holds it (M324).
    way: protectedAll || coming ? (next && !held && patrolAffordable(world, next) ? { label: 'Patrol a stretch of coast', act: 'reef:patrol' } : null)
      : liaisonFree && !job ? { label: 'Protect Maui Nui\'s grazing fish', act: 'reef:protect' }
      : next && patrolAffordable(world, next) ? { label: 'Patrol a stretch of coast now', act: 'reef:patrol' } : null,
    cost: liaisonFree && !coming && !protectedAll ? `a protected area ${dollars(consult)} and your liaison ${Math.round(ECONOMY.rule.consultationDays / 30)} months; or a patrol about ${dollars(patrolMonth)} a month` : next ? `a patrol about ${dollars(patrolMonth)} a month` : undefined,
    // The proposal under way told as under way, not as waiting (review 34: "a protected area waits until then" of itself).
    gives: coming ? undefined : liaisonFree && !protectedAll ? 'while your liaison consults, no volunteers can be set up' : !liaisonFree ? liaisonWaitWords(liaisonBusy(world) ?? { doing: 'is at work', until: epochDayOf(world), count: 1 }) : undefined,
    others: coming ? 'The protected area is under way: a patrol starts at once and keeps the rules already in force until it is.'
      : liaisonFree ? 'A protected area takes your liaison months with every island\'s communities and lasts; a patrol of a stretch of coast starts at once and lasts its term, paid by the day.'
      : noLiaison ? 'The conservancy employs no liaison: a patrol starts at once, paid by the day; a protected area waits for one hired.'
      : 'Your liaison is busy: a patrol starts at once, paid by the day; a protected area waits for the liaison.',
  };
}

/**
 * Where the reef's next patrol stretch goes (M327. GAME CHOICE: the Maui unit's patrol is a stretch of coast,
 * `PATROL_LEVELS`, so the reef's patrols are stretches, one to four, each sent to the coast not yet patrolled where the
 * most grazing fish are taken, a protected coast among them, as patrols keep its rule; a fixed number of the unit's
 * hours, its cost and its cut together, never all of Maui Nui's coast at once: the four islands' first patrols cost
 * $1,035,540 a year, MEASURED as their `costPerYear[1]` summed at seed 42's opening, more than the year's $720,000
 * grant): that island, what a stretch there keeps a month and what is taken there now; none where every coast is
 * patrolled. The reef card, the goals and the month's decision all read it, so the coast named is the coast patrolled.
 */
export function nextStretch(world: World): { island: ReefIsland; fewer: number; now: number } | null {
  const m = world.sanctuaries[0]?.management;
  if (!m) return null;
  const open = REEF_ISLANDS.filter((_, i) => (m.patrols[i] ?? 0) === 0)
    .map((island) => ({ island, cut: patrolCut(world, island, 1) }))
    .filter((x): x is { island: ReefIsland; cut: NonNullable<typeof x.cut> } => x.cut !== null && x.cut.fewer > 0)
    .sort((a, b) => b.cut.now - a.cut.now)[0];
  return open ? { island: open.island, fewer: open.cut.fewer, now: open.cut.now } : null;
}

/** The island with the most of its reef in the shallows, the heat's most exposed (`concernsOf`): the season's count's. */
export const mostExposed = (world: World): ReefIsland => REEF_ISLANDS.find((island) => concernsOf(world, island)?.mostExposed) ?? 'maui';

function season(world: World): Goal {
  const today = epochDayOf(world);
  const civil = civilFromEpochDay(today);
  const s = world.sanctuaries[0];
  const ops = world.operations;
  const exposed = mostExposed(world);
  const assessment = surveyCost('reefBleaching', ECONOMY.assessmentDays);
  const from = epochDayFromCivil(civil.year, SEASON.fromMonth, 1);
  const end = epochDayFromCivil(civil.year, SEASON.endMonth, SEASON.endDay);
  const others = `A coral count before July is the baseline the season's toll is read against; money for a bleaching check, ${dollars(assessment)}, keeps the choice open${ops.boats < 2 ? '; a second boat assesses while the first keeps the plan' : ''}.`;
  // An episode of heat at NOAA's first alert, whatever the date (review M278's: one running past 31 October dropped its
  // choice): its choice asked first, or told made.
  if ((s?.heatStress.peakDhw ?? 0) >= HEAT_ALERT_DHW) {
    if (ops.heatChoice === null) return { id: 'season', title: 'Meet the heat', state: 'Heat stress has reached NOAA\'s first alert: choose what you will know of its toll.', level: 'open', way: { label: 'The heat\'s choice', act: 'overview' }, others };
    return { id: 'season', title: 'Meet the heat', state: ops.heatChoice === 'assess' ? 'The heat came: its bleaching check chosen.' : 'The heat came: the plan kept, its toll for the next coral survey.', level: 'met', way: null, others };
  }
  if (today >= from && today <= end) return { id: 'season', title: 'Meet the warm season', state: `The warm season, to ${dateLabel(end)}: no alert yet.`, level: 'doing', way: null, others };
  // Before the season, or after it for the next: a baseline since the last season ended (review M278's: judged by the
  // calendar year, a count after one season was dropped on 1 January), on the island with the most shallow reef, and
  // the assessment's cost in hand.
  const year = today > end ? civil.year + 1 : civil.year;
  const since = (today > end ? end : epochDayFromCivil(civil.year - 1, SEASON.endMonth, SEASON.endDay)) + 1;
  // The whole reef's count (M327, one reef), its shallows the most exposed island's among them.
  const baseline = reefCoralHistory(world).some((p) => p.by === 'conservancy' && p.day >= since);
  const going = !baseline && reefUnderway(world, 'reefBenthic');
  const money = ops.funds >= assessment;
  return {
    id: 'season', title: `Prepare for the warm season of ${year}`,
    state: `${baseline ? 'The reef\'s coral counted since the last season' : going ? 'A coral survey of the reef under way' : `The reef's coral not counted since the last season, ${ISLAND_NAMES[exposed]}'s the most of its reef in the shallows`}; ${money ? 'an assessment\'s cost in hand' : `less than an assessment's ${dollars(assessment)} in hand`}.`,
    level: baseline && money ? 'met' : baseline || money || going ? 'doing' : 'open',
    way: !baseline && !going ? { label: 'Count the reef\'s coral', act: 'reef:survey:reefBenthic' } : !money ? { label: 'The accounts', act: 'operations' } : null,
    cost: !baseline && !going ? surveyWords('reefBenthic') : undefined,
    gives: !baseline && !going ? boatTradeoff(world) : undefined,
    others,
  };
}

function money(world: World): Goal {
  const ops = world.operations;
  const months = monthsInHand(world);
  // The funds' own levels (`fundsLevel`): sound at six months or more, under way at two.
  const level = ops.funds >= 0 && months >= FUNDS_MONTHS.good ? 'met' : ops.funds >= 0 && months >= FUNDS_MONTHS.fair ? 'doing' : 'open';
  return {
    id: 'money', title: 'Keep the program solvent',
    state: ops.funds < 0 ? `In debt, ${dollars(ops.funds)}: patrols lapse and surveys wait.` : Number.isFinite(months) ? `${dollars(ops.funds)} in hand, ${runwayWords(world)}.` : `${dollars(ops.funds)} in hand.`,
    level,
    way: level === 'met' ? null : { label: 'The accounts', act: 'operations' },
    others: `Each survey home brings a research grant of ${dollars(ECONOMY.researchGrantPerSurvey)}; the tourism partners pay by the coral you know; patrols, volunteers and staff cost by the day, and the State's grant comes each 1 July.`,
  };
}

/**
 * A shore cleanup's days as the goal's way sends it (M309, the field loop's F8, a game's choice: the shortest trip the
 * plan offers, `ECONOMY.surveyDays`' first, so a cleanup sent after the first survey is home within the first month).
 */
export const SHORE_DAYS = 5;

/**
 * Derelict nets cleared from a shore (M309, the field loop's F8, `docs/FIELD_LOOP_ROADMAP.md` "The loop's design"): the
 * reef island whose shore holds the most tonnes today (`shoreDebrisT`, ties in `REEF_ISLANDS`' order), its way that
 * island's cleanup at `SHORE_DAYS`. Waits while any cleanup is planned or at sea; met only while no shore holds a trip's
 * clearing, the gear coming ashore all year (M307) and the goal open again as it does. The last goal, so the surveys and the protection lead and the mission fills the boat's idle days.
 */
function shore(world: World): Goal {
  const ops = world.operations;
  const tonnes = REEF_ISLANDS.map((island) => ({ island, t: shoreDebrisT(world, island) }));
  const most = tonnes.reduce((a, b) => (b.t > a.t ? b : a));
  const name = ISLAND_NAMES[most.island];
  const sea = atSea(ops).find((j) => j.survey === 'shoreCleanup');
  const planned = ops.plan.find((p) => p.kind === 'shoreCleanup');
  const going = sea?.island ?? planned?.island ?? null;
  const last = ops.finished.filter((f) => isCleanupHome(f) && f.island !== null).at(-1);
  const lies = `About ${Math.round(most.t)} t of derelict nets lie on ${name}'s shore`;
  const rate = CLEARED_T_PER_DAY === 0.5 ? 'half a tonne' : `${CLEARED_T_PER_DAY} t`;
  // A trip offered only where it fills its days (its check, M309: the pacing's player had sent 60 a year, the late ones
  // clearing a few tenths of a tonne): no shore holding a trip's clearing, the goal is met until the nets come ashore
  // again (a game choice, M178; the shores' cleanups repeat "every three years or so" at Kanapou, M307).
  const trip = CLEARED_T_PER_DAY * SHORE_DAYS;
  if (!going && most.t < trip) {
    return { id: 'shore', title: 'Clear derelict nets from a shore', state: `Every shore cleared to under ${trip.toFixed(1)} t, a trip's clearing: nets come ashore again all year.`, level: 'met', way: null, others: 'Each shore is its own trip, on the boat the surveys use.' };
  }
  // Recommended only where the year's money holds it to the grant (M329: the year probe's player, sent a cleanup each
  // time the boat came home, ran the spring's money short and every later decision with it); the boat's card still
  // offers it.
  const cost = surveyCost('shoreCleanup', SHORE_DAYS);
  if (!going && yearMoney(world).spare < cost) {
    return { id: 'shore', title: 'Clear derelict nets from a shore', state: `${lies}; a cleanup's ${dollars(cost)} is more than the year's money spares before the grant.`, level: 'open', way: null, others: 'Each shore is its own trip, on the boat the surveys use. Nets come ashore all year, so no shore stays clear.' };
  }
  return {
    id: 'shore', title: 'Clear derelict nets from a shore',
    state: going ? `${ISLAND_NAMES[going]}'s shore cleanup ${sea ? 'is at sea' : 'waits in the plan'}. ${lies}.`
      : last ? `${lies}. The last cleanup: ${(last.tonnes ?? 0).toFixed(1)} t removed from ${ISLAND_NAMES[last.island as ReefIsland]}'s shore on ${dateLabel(last.endDay)}.`
      : `${lies}; the boat clears ${rate} a day.`,
    level: going ? 'doing' : 'open',
    way: going ? null : { label: `Clear ${name}'s shore`, act: 'reef:cleanup' },
    others: 'Each shore is its own trip, on the boat the surveys use. Nets come ashore all year, so no shore stays clear.',
    cost: going ? undefined : `${dollars(surveyCost('shoreCleanup', SHORE_DAYS))}, a boat ${SHORE_DAYS} days`,
    gives: going ? undefined : boatTradeoff(world),
  };
}

/**
 * Whether one more island's patrol leaves the funds half a year of running costs (M317, G7: the first session's probe
 * found the guidance buying a patrol on every island in one day, $85,113 a month, two months in hand). GAME CHOICE: the
 * funds' own "good" line (`FUNDS_MONTHS.good`) after the patrol's month is added; below it the patrol is still on the
 * island's page, not recommended. At the opening's $360,000 one patrol leaves 7 months, a second 5.
 */
export function patrolAffordable(world: World, island: ReefIsland): boolean {
  const added = patrolHoursAdded(island, 1) * ECONOMY.officerHour * MONTH_DAYS / 365;
  return world.operations.funds >= 0 && world.operations.funds / (recurringPerMonth(world).total + added) >= FUNDS_MONTHS.good && patrolFitsYear(world, island);
}

/**
 * Whether the patrol the guidance opens (the Protect page's, its last term, `ECONOMY.patrolTerms`) fits the year's money
 * to the next grant, as that page reckons it (M324's review: put while the funds' row read short; its second review:
 * reckoned to the grant, a year, the page's patrol 90 days).
 */
function patrolFitsYear(world: World, island: ReefIsland): boolean {
  const today = epochDayOf(world);
  const days = Math.min(nextGrantDay(today), today + (ECONOMY.patrolTerms.at(-1) as number)) - today;
  return yearMoney(world).spare >= (patrolHoursAdded(island, 1) * ECONOMY.officerHour * days) / 365;
}

/** Whether the boats' plan holds room for one more trip (M317, G7: the card offered a count the core refused, "the plan holds 4 surveys already"). */
export const planRoom = (world: World): boolean => world.operations.plan.length < ECONOMY.planLength;

/** The words for a trip the full plan holds back (M317). */
const planFullWords = (): string => ` The boats' plan holds ${ECONOMY.planLength} trips already: this one waits for room.`;

/**
 * The sanctuary's goals, the brief's four in its order, then the shore's mission last (M309); none where it holds no reef.
 */
export function goalsOf(world: World): Goal[] {
  const s = world.sanctuaries[0];
  if (!s?.coral || !s.fish) return [];
  const goals = [decline(world), pressure(world), evaluate(world), season(world), money(world), shore(world)].filter((g): g is Goal => g !== null);
  // A boat trip recommended only while the plan has room (M317's review: the home card, no island chosen, still offered
  // the trip the core refused, "the plan holds 4 surveys already"); the goal stands, its way waits.
  if (planRoom(world)) return goals;
  return goals.map((g) => (g.way && /^reef:(survey|cleanup)\b/.test(g.way.act) ? { ...g, way: null, state: `${g.state}${planFullWords()}` } : g));
}

/** Whether the first chapter's guidance is still under way: the goals then wait behind it (review M278's: two "do this now"s at once). */
export const inTutorial = (world: World): boolean => currentObjective(world)?.chapter === 'firstSurvey';

/**
 * The goal recommended now: the one that cannot wait first (the heat's choice, money short), else the first not met
 * with a way to it now; none where every goal is met or waits.
 */
export function recommendedGoal(goals: readonly Goal[]): Goal | null {
  const urgent = goals.find((g) => g.level === 'open' && g.way && (g.id === 'season' && g.way.act === 'overview' || g.id === 'money'));
  // The money short but not urgent (two to six months in hand) is no step to recommend (M327, GAME CHOICE: its way, the
  // accounts, holds nothing to press, and `review:session` met the card asking it four times on 1 February); it stays
  // in the goals' list.
  return urgent ?? goals.find((g) => g.level !== 'met' && g.way && g.id !== 'money') ?? null;
}
